// 数组处理工具函数

/**
 * 将符合条件的内容放到最前面
 * @param arr 数组
 * @param conditionFn 条件函数
 * @example putFirst([1, 2, 3, 4, 5], (i)=>i===5) 返回 [5, 1, 2, 3, 4]
 */
export function putFirst<T>(arr: T[], conditionFn: (item: T) => boolean) {
  // 使用 filter 方法找到符合条件的元素
  const matchedElements = arr.filter(conditionFn);

  // 使用 filter 方法找到不符合条件的元素
  const nonMatchedElements = arr.filter((element) => !conditionFn(element));

  // 将符合条件的元素放到数组最前面
  return [...matchedElements, ...nonMatchedElements];
}

/**
 * 数组元素移动工具函数
 * @param  array - 要操作的数组
 * @param  fn1 - 用于判断要移动的元素的函数
 * @param  fn2 - 用于判断目标位置元素的函数
 * @param  isBefore - 是否移动到目标元素之前
 * @returns 返回新的数组
 */
export const arrayMove = <T>(
  array: T[],
  fn1: (item: T) => boolean,
  fn2: (item: T) => boolean,
  isBefore = true
): T[] => {
  if (!Array.isArray(array) || array.length === 0) {
    return array;
  }

  // 创建数组副本
  const result = [...array];

  // 找到要移动的元素索引
  const moveIndex = result.findIndex(fn1);
  if (moveIndex === -1) return result;

  // 找到目标位置的索引
  const targetIndex = result.findIndex(fn2);
  if (targetIndex === -1) return result;

  // 取出要移动的元素
  const [moveItem] = result.splice(moveIndex, 1);

  // 计算新的插入位置
  const insertIndex =
    moveIndex < targetIndex
      ? isBefore
        ? targetIndex - 1
        : targetIndex
      : isBefore
      ? targetIndex
      : targetIndex + 1;

  // 插入元素到新位置
  result.splice(insertIndex, 0, moveItem);

  return result;
};

/**
 * 查找相邻的元素
 * @param arr - 要查找的数组
 * @param predicate - 用于定位目标元素的判断函数
 * @param option - 配置选项
 * @param option.offset - 查找方向和步长，正数向后查找，负数向前查找，默认为 1
 * @param option.isSatisfy - 用于判断相邻元素是否满足条件的函数，不传则返回第一个相邻元素
 * @returns 返回满足条件的相邻元素，如果没找到则返回 undefined
 * @example
 * // 查找数字 3 后面第一个偶数
 * findAdjacentEle([1, 2, 3, 4, 5], (i) => i === 3, { offset: 1, isSatisfy: (i) => i % 2 === 0 }) // 返回 4
 */
export const findAdjacentEle = <T>(
  arr: T[],
  predicate: (item: T) => boolean,
  {
    offset = 1,
    // 判断满足条件
    isSatisfy,
  }: {
    offset?: number;
    isSatisfy?: (item: T) => boolean;
  }
) => {
  const targetIndex = arr.findIndex(predicate);
  if (targetIndex === -1) return undefined;

  // 根据 offset 方向查找下一个满足条件的元素
  const step = offset > 0 ? 1 : -1;
  for (let i = targetIndex + step; i >= 0 && i < arr.length; i += step) {
    const item = arr[i];
    if (!isSatisfy) {
      // 如果 isSatisfy 不存在，则返回第一个相邻元素
      return item;
    }
    // 如果 isSatisfy 存在且满足，则返回该元素
    if (isSatisfy?.(item)) {
      return item;
    }
  }
  return undefined;
};
